import type { Breadcrumb, Event } from "@sentry/react";

export const isReportLocation = (path: string) =>
  /^\/reports(?:[/?#]|$)/.test(path);
export const isRenewalDraftLocation = (path: string) =>
  /^\/renewal-drafts(?:[/?#]|$)/.test(path);
const hasRenewalDraftUrl = (value: string) =>
  /\/(?:api\/hazel\/renewals\/drafts|renewal-drafts)(?:[/?#"\\]|$)/.test(value);
// The public signing page (the tenant's, and an owner's without an account) and the tenant's
// Ejari fee link (/pay/{reference}, the same reference): the reference in the URL is the
// credential, and the pages carry the contract and the signer's details.
export const isSigningLocation = (path: string) =>
  /^\/(?:sign|pay)(?:[/?#]|$)/.test(path);
// The pages' own addresses carry a 32-hex reference; the app's /landlord/properties/sign/{id}
// carries an offer id, which is no credential.
const hasSigningUrl = (value: string) =>
  /\/(?:api\/signing(?:[/?#"\\]|$)|(?:sign|pay)\/[0-9a-f]{32})/i.test(value);
const hasReportUrl = (value: string) =>
  /\/(?:api\/)?reports(?:[/?#"\\]|$)/.test(value);

// Preserve route names and static asset locations, never report IDs or URL secrets.
export function reportTelemetryUrl(value: string): string {
  try {
    const url = new URL(value, "https://telemetry.invalid");
    const path = url.pathname
      .replace(/^(\/(?:api\/)?reports)\/[^/]+/, "$1/:reportId")
      .replace(
        /^(\/(?:api\/hazel\/renewals\/drafts|renewal-drafts))\/[^/]+/,
        "$1/:draftId",
      )
      .replace(/^(\/(?:api\/signing|sign|pay))\/[^/]+/, "$1/:reference");
    return `${url.origin === "https://telemetry.invalid" ? "" : url.origin}${path}`;
  } catch {
    return "[private URL]";
  }
}

export function scrubReportBreadcrumb(
  breadcrumb: Breadcrumb,
  path: string,
): Breadcrumb | null {
  const privatePage =
    isReportLocation(path) ||
    isRenewalDraftLocation(path) ||
    isSigningLocation(path);
  const data = JSON.stringify(breadcrumb.data ?? {});
  const privateRequest =
    hasReportUrl(data) || hasRenewalDraftUrl(data) || hasSigningUrl(data);
  if (!privatePage && !privateRequest) return breadcrumb;
  if (breadcrumb.category === "navigation")
    return {
      timestamp: breadcrumb.timestamp,
      category: "navigation",
      data: {
        from: reportTelemetryUrl(String(breadcrumb.data?.from ?? "")),
        to: reportTelemetryUrl(String(breadcrumb.data?.to ?? "")),
      },
    };
  if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr")
    return {
      timestamp: breadcrumb.timestamp,
      category: breadcrumb.category,
      type: breadcrumb.type,
      data: {
        url: reportTelemetryUrl(String(breadcrumb.data?.url ?? "")),
        method: breadcrumb.data?.method,
        status_code: breadcrumb.data?.status_code,
      },
    };
  // Console arguments, UI labels and custom breadcrumbs can contain report content.
  return null;
}

export function scrubReportEvent<T extends Event>(event: T, path: string): T {
  const privateEvent =
    isReportLocation(path) ||
    isRenewalDraftLocation(path) ||
    isSigningLocation(path) ||
    hasRenewalDraftUrl(JSON.stringify(event)) ||
    hasSigningUrl(JSON.stringify(event)) ||
    !!event.tags?.report_operation ||
    hasReportUrl(event.request?.url ?? "") ||
    hasReportUrl(JSON.stringify(event.spans ?? [])) ||
    hasReportUrl(JSON.stringify(event.breadcrumbs ?? []));
  const breadcrumbs = event.breadcrumbs?.flatMap((breadcrumb) => {
    const safe = scrubReportBreadcrumb(
      breadcrumb,
      privateEvent ? "/reports" : path,
    );
    return safe ? [safe] : [];
  });
  if (!privateEvent) return { ...event, breadcrumbs };
  // Allowlist technical diagnostics. Error messages, user context, request bodies,
  // headers, console arguments and arbitrary extras can all carry private data.
  const trace = event.contexts?.trace;
  const sanitized: Event = {
    event_id: event.event_id,
    timestamp: event.timestamp,
    type: event.type,
    platform: event.platform,
    level: event.level,
    environment: event.environment,
    release: event.release,
    dist: event.dist,
    transaction: reportTelemetryUrl(event.transaction ?? path),
    message: event.message ? "Report page error" : undefined,
    exception: event.exception
      ? {
          values: event.exception.values?.map((value) => ({
            type: value.type,
            value: "Report page error (private details removed)",
            stacktrace: value.stacktrace
              ? {
                  frames: value.stacktrace.frames?.map((frame) => ({
                    filename: frame.filename
                      ? reportTelemetryUrl(frame.filename)
                      : undefined,
                    abs_path: frame.abs_path
                      ? reportTelemetryUrl(frame.abs_path)
                      : undefined,
                    function: frame.function,
                    lineno: frame.lineno,
                    colno: frame.colno,
                    in_app: frame.in_app,
                  })),
                }
              : undefined,
          })),
        }
      : undefined,
    request: event.request
      ? {
          url: reportTelemetryUrl(event.request.url ?? path),
          method: event.request.method,
        }
      : undefined,
    contexts: trace
      ? {
          trace: {
            trace_id: trace.trace_id,
            span_id: trace.span_id,
            op: trace.op,
            status: trace.status,
          },
        }
      : undefined,
    tags: {
      report_operation: event.tags?.report_operation,
      http_status: event.tags?.http_status,
    },
    breadcrumbs,
    spans: event.spans?.map((span) => ({
      trace_id: span.trace_id,
      span_id: span.span_id,
      parent_span_id: span.parent_span_id,
      start_timestamp: span.start_timestamp,
      timestamp: span.timestamp,
      op: span.op,
      status: span.status,
      description: span.op,
      data: {},
    })),
    start_timestamp: event.start_timestamp,
  };
  return sanitized as T;
}

// Keep error telemetry, but omit console logs and replay recordings on private
// report, renewal-draft and signing screens. Also drop delayed records that
// still refer to one of their URLs.
export function omitReportRecording(value: unknown, path: string): boolean {
  const serialized = JSON.stringify(value) ?? "";
  return (
    isReportLocation(path) ||
    isRenewalDraftLocation(path) ||
    isSigningLocation(path) ||
    hasReportUrl(serialized) ||
    hasRenewalDraftUrl(serialized) ||
    hasSigningUrl(serialized)
  );
}
